import { format } from "date-fns"
import Decimal from "decimal.js"
import {
  FIXED_DISCOUNT_TYPE,
  PERCENT_DISCOUNT_TYPE,
} from "src/main/Billing/Items/EditInstallmentStay/EditInstallmentDiscountsModal/helpers"
import { dollarsToCents } from "src/main/Billing/Items/helpers"
import { DEFAULT_ONE_TIME_PRICING_STRUCTURE } from "src/main/Billing/constants"

export const nameToInitials = (name = "") => {
  if (!name) return ""
  if (name.length === 0) return ""

  return name
    .trim()
    .replace(/[^a-zA-Z ]/, "")
    .split(" ")
    .filter((s) => s.length)
    .map((n) => n[0])
    .map((c) => c.toUpperCase())
    .filter((c, i) => i < 2)
    .join("")
}

function transformServerCartItemToTxn(
  cartItem,
  cartId,
  serviceDate,
  useNewTaxExperience
) {
  const {
    quantity,
    pricePrecision: precision,
    subtotal,
    total,
    taxRate: tax,
    discount: manualDiscount,
    taxAmount,
    note,
    name,
    productId,
    price,
    discountTemplate,
    taxes,
  } = cartItem

  const discounts = []
  if (discountTemplate) {
    const { percentAmount, label, amount } = discountTemplate
    const appliedDiscountTemplateAttributes = Object.fromEntries(
      Object.entries({
        amount,
        percent_amount: !percentAmount ? null : percentAmount,
        source: discountTemplate.source,
        pricing_structure: discountTemplate.pricingStructure,
        quantity_limit: discountTemplate.quantityLimit,
        quantity_limit_type: discountTemplate.quantityLimitType,
      }).filter(([_, value]) => !!value)
    )

    if (percentAmount) {
      discounts.push({
        type: PERCENT_DISCOUNT_TYPE,
        name: label,
        amount: Math.round(percentAmount * subtotal),
        percent_amount: percentAmount,
        applied_discount_template_attributes: appliedDiscountTemplateAttributes,
      })
    } else {
      discounts.push({
        type: FIXED_DISCOUNT_TYPE,
        name: label,
        amount: Math.round(amount * quantity),
        applied_discount_template_attributes: appliedDiscountTemplateAttributes,
      })
    }
  }

  if (manualDiscount) {
    const automatedDiscountAmount = discounts.reduce(
      (acc, discount) => acc + discount.amount,
      0
    )
    const decimalPercent = Decimal.div(manualDiscount, 100).toNumber()
    discounts.push({
      type: PERCENT_DISCOUNT_TYPE,
      name: `Point of Sale Discount (${name})`,
      amount: Math.round(decimalPercent * (subtotal - automatedDiscountAmount)),
      percent_amount: decimalPercent,
    })
  }

  const appliedDiscounts = discounts.map((discount, index) => ({
    ...discount,
    apply_order: index,
  }))

  // When using new tax experience, pass individual taxes and set tax_rate to null
  // Otherwise, pass combined tax_rate as before
  const productSaleAttributes = {
    tax_amount: taxAmount,
    original_amount: subtotal,
    price_per_unit: price,
    price_precision: precision,
    pricing_structure: DEFAULT_ONE_TIME_PRICING_STRUCTURE,
    product_id: productId,
    service_start_date: serviceDate,
    service_end_date: serviceDate,
    point_of_sale_cart_id: cartId,
    name,
    note,
    quantity,
    product_sale_discounts_attributes: appliedDiscounts,
  }

  if (useNewTaxExperience) {
    productSaleAttributes.tax_rate = null
    productSaleAttributes.product_sale_taxes_attributes = taxes.map((tax) => ({
      rate: tax.rate,
      tax_amount: tax.taxAmount,
      tax_type: tax.taxType,
      name: tax.name,
      source_type: tax.sourceType,
      source_id: tax.sourceId,
    }))
  } else {
    productSaleAttributes.tax_rate = tax
  }

  return {
    type: "Billing::ProductSaleTxn",
    amount: total,
    product_sale_attributes: productSaleAttributes,
  }
}

export const formatServerCartToTxns = ({
  cart,
  cartId,
  tip,
  tipItem,
  useNewTaxExperience = false,
}) => {
  // DEFAULTING DUE DATE TO TODAY FOR POS
  // Defining `serviceDate` once out here so as we iterate we can guarantee
  // all txns will get the same service date
  const serviceDate = format(new Date(), "yyyy-MM-dd")

  const tipPresent = Boolean(tip) && tip !== "0.00"
  const cartItems = [...cart]

  if (tipPresent) {
    const tipAsCents = dollarsToCents(tip)
    const serverTipItem = {
      discount: 0,
      discountAmount: 0,
      name: "Tip",
      price: tipAsCents,
      pricePrecision: "cents",
      productId: tipItem.productId,
      quantity: 1,
      taxRate: 0,
      taxAmount: 0,
      subtotal: tipAsCents,
      total: tipAsCents,
      taxes: [],
    }

    cartItems.push(serverTipItem)
  }

  const txns = cartItems.map((cartItem) =>
    transformServerCartItemToTxn(
      cartItem,
      cartId,
      serviceDate,
      useNewTaxExperience
    )
  )

  const amount = new Decimal(
    cartItems.reduce((acc, item) => acc + item.total, 0)
  )
    .negated()
    .toNumber()

  return { txns, amount }
}
